
import { useState, useEffect, useCallback, useRef } from "react";
import { GameStatus, GuessHistory, ValidatedGuess, KeyboardStatus, PreviousWord } from "@/lib/types/GameTypes";
import { toast } from "sonner";
import * as CookieManager from "@/lib/managers/CookieManager";
import { updateKeyboardStatusFromGuesses } from "../managers/KeyboardStateManager";
import { useGameMode } from "@/contexts/GameModeContext";
import { createStorageAdapter } from "@/lib/storage/StorageAdapter";
import { formatDate, getEffectiveDate } from "@/lib/dateUtils";
import { CompletionStateManager } from "../managers/CompletionStateManager";

export const useGameInitialization = (
  currentWord: string
) => {
  const { currentMode, getModeConfig } = useGameMode();
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const previousModeRef = useRef<string | null>(null);
  const modeConfig = getModeConfig(currentMode);
  const storageAdapter = useRef(createStorageAdapter(currentMode, modeConfig));
  const forceInitRef = useRef(false);

  // Update storage adapter when mode changes
  useEffect(() => {
    storageAdapter.current = createStorageAdapter(currentMode, modeConfig);
    
    // Check if this is a mode change
    if (previousModeRef.current !== null && previousModeRef.current !== currentMode) {
      console.log(`Mode change detected in useGameInitialization: ${previousModeRef.current} -> ${currentMode}`, {
        timestamp: new Date().toISOString()
      });
      // Force re-initialization
      forceInitRef.current = true;
      setIsInitialized(false);
    }
    
    previousModeRef.current = currentMode;
  }, [currentMode, modeConfig]);

  // Listen for mode switch events
  useEffect(() => {
    const handleModeSwitch = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('useGameInitialization received mode switched event', {
        detail: customEvent.detail,
        timestamp: new Date().toISOString()
      });
      
      // Force re-initialization
      forceInitRef.current = true;
      setIsInitialized(false);
      setIsLoading(true);
    };
    
    window.addEventListener('babblegram_mode_switched', handleModeSwitch);
    
    return () => {
      window.removeEventListener('babblegram_mode_switched', handleModeSwitch);
    };
  }, []);

  // Convert to useCallback to prevent recreation on each render
  const initializeGameState = useCallback(async (
    setGuesses: (guesses: ValidatedGuess[]) => void,
    setKeyboardStatus: (status: KeyboardStatus) => void,
    setPreviousWord: (word: PreviousWord) => void,
    setLevelAttempts: (attempts: number[]) => void,
    setGuessHistory: (history: GuessHistory) => void,
    setGameStatus: (status: GameStatus) => void
  ) => {
    // If already initialized and not forcing, and we have a current word, do nothing
    if (isInitialized && !forceInitRef.current && currentWord) {
      console.log('Skipping initialization - already initialized', {
        isInitialized,
        forceInit: forceInitRef.current,
        hasWord: !!currentWord,
        mode: currentMode,
        timestamp: new Date().toISOString()
      });
      return;
    }

    try {
      // Set loading state
      setIsLoading(true);
      
      console.log('Initializing game state for word:', currentWord, {
        mode: currentMode,
        storagePrefix: modeConfig.storagePrefix,
        forceInit: forceInitRef.current,
        timestamp: new Date().toISOString()
      });
      
      // Reset force init flag
      forceInitRef.current = false;
      
      // Check for mode switch in progress
      const isModeSwitch = localStorage.getItem('babblegram_mode_switch_in_progress') === 'true';
      if (isModeSwitch) {
        console.log('Mode switch in progress detected during initialization', {
          timestamp: new Date().toISOString()
        });
        // Clear the flag
        localStorage.removeItem('babblegram_mode_switch_in_progress');
      }
      
      // Update previous mode reference
      previousModeRef.current = currentMode;
      
      // Get state from storage using proper adapter for this mode
      const gameStatus = storageAdapter.current.get('game_status');
      const guessHistoryStr = storageAdapter.current.get('guess_history');
      const guessHistory = guessHistoryStr ? JSON.parse(guessHistoryStr) : [];
      const levelAttemptsStr = storageAdapter.current.get('level_attempts');
      const levelAttempts = levelAttemptsStr ? JSON.parse(levelAttemptsStr) : [];
      const previousWordStr = storageAdapter.current.get('previous_word');
      const previousWord = previousWordStr ? JSON.parse(previousWordStr) : null;
      const currentLevel = storageAdapter.current.get('current_level');
      
      console.log('Retrieved state from storage for mode', {
        mode: currentMode,
        hasGuessHistory: !!guessHistoryStr,
        guessHistoryEntries: guessHistory?.length || 0,
        hasGameStatus: !!gameStatus,
        currentLevel,
        timestamp: new Date().toISOString()
      });

      // DATE-AWARE: In DAILY mode, ignore stale state from previous day to prevent re-stamping
      const savedDate = storageAdapter.current.get('game_persistence_date');
      const today = formatDate(getEffectiveDate());
      if (currentMode === 'daily' && (!savedDate || savedDate !== today)) {
        // Check if the game is already completed for today before resetting
        const alreadyCompleted = CompletionStateManager.hasCompletedGame('daily');
        console.log('Daily mode stale persistence detected in useGameInitialization.', {
          savedDate,
          today,
          alreadyCompleted,
          timestamp: new Date().toISOString()
        });
        
        if (alreadyCompleted) {
          console.log('Game already completed today - preserving completion state');
          setIsInitialized(true);
          setIsLoading(false);
          return;
        }
        
        // Reset transient state instead of loading stale values
        setGuessHistory([]);
        setLevelAttempts([]);
        setPreviousWord(null);
        setKeyboardStatus({} as KeyboardStatus);
        setGameStatus('playing');
        try {
          storageAdapter.current.set('game_status', 'playing');
          storageAdapter.current.set('game_completion_state', 'false');
        } catch {}
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }
      
      // Set the guess history first
      if (guessHistory && guessHistory.length > 0) {
        setGuessHistory(guessHistory);
        console.log('Set guess history with entries:', guessHistory.length, {
          mode: currentMode,
          timestamp: new Date().toISOString()
        });
      } else {
        setGuessHistory([]);
      }
      
      // Set level attempts
      setLevelAttempts(levelAttempts || []);
      
      // Set previous word if available
      if (previousWord) {
        setPreviousWord(previousWord);
      }
      
      // Set game status - Fix: validate that gameStatus is a valid GameStatus
      const validatedStatus = validateGameStatus(gameStatus);
      setGameStatus(validatedStatus);
      
      // Find guesses for current level and word
      let currentGuesses: ValidatedGuess[] = [];
      
      if (currentLevel && currentWord) {
        const levelHistory = guessHistory.find(
          h => h.level === parseInt(currentLevel) && h.word === currentWord
        );
        
        if (levelHistory && levelHistory.guesses) {
          currentGuesses = JSON.parse(JSON.stringify(levelHistory.guesses));
          console.log('Found level-specific guesses:', {
            level: currentLevel,
            word: currentWord,
            mode: currentMode,
            guessCount: currentGuesses.length,
            guessDetails: currentGuesses.map(g => g.map(l => l.letter).join('')),
            timestamp: new Date().toISOString()
          });
        }
      }
      
      // Set the guesses
      setGuesses(currentGuesses);
      
      // Update keyboard status based on guesses
      if (currentGuesses.length > 0) {
        const keyboardState = updateKeyboardStatusFromGuesses(currentGuesses);
        console.log('Setting keyboard status from guesses:', {
          keyCount: Object.keys(keyboardState).length,
          guessCount: currentGuesses.length,
          mode: currentMode,
          timestamp: new Date().toISOString()
        });
        setKeyboardStatus(keyboardState);
      } else {
        // Reset keyboard status if no guesses
        setKeyboardStatus({});
      }
      
      // Mark as initialized
      setIsInitialized(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Error initializing game state:', error);
      setInitError(error instanceof Error ? error : new Error('Failed to initialize game state'));
      toast.error("There was an error loading your game. Please refresh the page.");
      setIsLoading(false);
    }
  }, [currentWord, currentMode, modeConfig, isInitialized]);

  // Helper function to validate game status
  const validateGameStatus = (status: string | null): GameStatus => {
    const validStatuses: GameStatus[] = ['playing', 'won', 'lost'];
    return validStatuses.includes(status as GameStatus) ? status as GameStatus : 'playing';
  };

  return {
    isInitialized,
    initError,
    isLoading,
    initializeGameState,
    storageAdapter: storageAdapter.current
  };
};
